import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Download,
  Ellipsis,
  Info,
  Plus,
  Share2,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { overlay } from 'overlay-kit';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import { AvartarType } from '@/components/Avatar/avatarConst';
import ProfileAvatar from '@/components/Avatar/ProfileAvatar';
import Transition from '@/components/Common/Transition';
import { ContentEditable } from '@/components/ContentEditable';
import { Button } from '@/components/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import AMP_KEYS from '@/configs/amp-track-keys';
import { studioQueryKeys } from '@/configs/react-query-keys/studio';
import { reportQueryKeys } from '@/entities/report';
import { SharedLinkDialog } from '@/entities/shared-link/ui/shared-link-dialog';
import {
  deleteStudioItemMutationOptions,
  exportStudioItemToPdf,
  updateStudioItemTitleMutationOptions,
} from '@/entities/studio';
import { StudioItemSourceView } from '@/features/Studio/components/SourceView';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { cn } from '@/lib/cn';
import service from '@/services';
import { Breadcrumb } from '@/shared/ui';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';
import { isEmptyString } from '@/utils/util';

import type { HeySynclyStudioItem } from '../../model';
import {
  stripStudioItemCategoryTags,
  studioItemMarkdownToHtml,
} from './studio-item-markdown';

export const StudioItemDetailHeader = (props: {
  className?: string;
  item?: HeySynclyStudioItem;
  onClose?: () => void;
  compactActions?: boolean;
}) => {
  const queryClient = useQueryClient();
  const updateStudioItemTitleMutation = useMutation(
    updateStudioItemTitleMutationOptions(queryClient),
  );
  const deleteStudioItemMutation = useMutation(
    deleteStudioItemMutationOptions(queryClient),
  );

  const setDetailData = (data: HeySynclyStudioItem) => {
    queryClient.setQueriesData<HeySynclyStudioItem>(
      { queryKey: [studioQueryKeys.STUDIO_ITEM_DETAIL, data.alias] },
      () => data,
    );
  };

  const [editMode, setEditMode] = useState(false);

  const onTitleChange = async (title: string) => {
    if (!props.item) return;
    const res = await updateStudioItemTitleMutation.mutateAsync({
      item_id: props.item.alias,
      title,
    });
    setDetailData(res as HeySynclyStudioItem);
  };

  const onDelete = async (itemId: string) => {
    await deleteStudioItemMutation.mutateAsync({
      item_id: itemId,
    });
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-2 px-8 py-5',
        props.className,
      )}
    >
      {props.onClose && props.item?.is_draft && (
        <HeaderIconButton
          icon={<X className="size-4" />}
          label="Close"
          onClick={props.onClose}
        />
      )}
      {!props.item?.is_draft && (
        <StudioItemTitleBreadcrumb
          title={props.item?.title || ''}
          onTitleChange={onTitleChange}
          editMode={editMode}
          setEditMode={setEditMode}
        />
      )}
      {props.item && (
        <RightActions
          compactActions={props.compactActions ?? false}
          item={props.item}
          onDelete={onDelete}
          setEditMode={setEditMode}
          setDetailData={setDetailData}
        />
      )}
    </div>
  );
};

const StudioItemTitleBreadcrumb = (props: {
  title: string;
  onTitleChange: (title: string) => void;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
}) => {
  const { title, onTitleChange, editMode, setEditMode } = props;
  const wr = useWorkspaceRouter();
  const [tempTitle, setTempTitle] = useState(title);

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  const onTitleChangeComplete = () => {
    setEditMode(false);
    if (isEmptyString(tempTitle)) {
      setTempTitle(title);
      toast('Title cannot be empty', 'fail');
      return;
    }
    toast('Title updated successfully', 'success');
    void onTitleChange(tempTitle);
  };

  return (
    <div className="flex items-center gap-2">
      <Breadcrumb
        label="Reports"
        onClick={() => {
          void wr.push('/reports');
        }}
        ariaLabel="Go to reports"
      />
      <span className="text-border">/</span>
      <div className="shrink-0 w-full min-w-0">
        {editMode ? (
          <Transition status>
            <ContentEditable
              autoFocus
              className="w-90"
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onBlur={() => onTitleChangeComplete()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }

                if (e.key === 'Escape') {
                  setEditMode(false);
                }
              }}
            />
          </Transition>
        ) : (
          <Breadcrumb
            label={tempTitle}
            onClick={() => setEditMode(true)}
            className="shrink-0 w-90"
            current
          />
        )}
      </div>
    </div>
  );
};

const RightActions = (props: {
  compactActions: boolean;
  item: HeySynclyStudioItem;
  onDelete: (itemId: string) => Promise<void>;
  setEditMode: (editMode: boolean) => void;
  setDetailData: (data: HeySynclyStudioItem) => void;
}) => {
  const wr = useWorkspaceRouter();
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const studioItemDetailQueryKey = [
    studioQueryKeys.STUDIO_ITEM_DETAIL,
    props.item.alias,
  ] as const;

  const isUpVoted = props.item.vote_up === true;
  const isDownVoted = props.item.vote_up === false;

  const updateCachedStudioItem = (
    updater: (current: HeySynclyStudioItem) => HeySynclyStudioItem,
  ) => {
    queryClient.setQueriesData<HeySynclyStudioItem>(
      { queryKey: studioItemDetailQueryKey },
      (current) => (current ? updater(current) : current),
    );
  };

  const voteMutation = useMutation({
    mutationFn: async (vote_up?: boolean) =>
      service.studio.VoteHeySynclyStudioItem(props.item.alias, vote_up),
    onMutate: async (vote_up) => {
      await queryClient.cancelQueries({
        queryKey: studioItemDetailQueryKey,
      });

      const previousStudioItems =
        queryClient.getQueriesData<HeySynclyStudioItem>({
          queryKey: studioItemDetailQueryKey,
        });

      updateCachedStudioItem((current) => ({
        ...current,
        vote_up,
      }));

      return { previousStudioItems };
    },
    onError: (_error, _vote_up, context) => {
      context?.previousStudioItems.forEach(([queryKey, previousData]) => {
        queryClient.setQueryData(queryKey, previousData);
      });
      toast('Failed to update vote.', 'fail');
    },
    onSuccess: (data) => {
      props.setDetailData(data);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: studioItemDetailQueryKey,
      });
    },
  });

  const handleThumbsUp = () => {
    void track(AMP_KEYS.STUDIO_ITEM_VOTE_UP_CLICK, {
      from: props.item.is_draft ? 'in_chat' : 'detail',
      alias: props.item.alias,
    });
    void voteMutation.mutateAsync(isUpVoted ? undefined : true);
  };

  const handleThumbsDown = () => {
    void track(AMP_KEYS.STUDIO_ITEM_VOTE_DOWN_CLICK, {
      from: props.item.is_draft ? 'in_chat' : 'detail',
      alias: props.item.alias,
    });
    void voteMutation.mutateAsync(isDownVoted ? undefined : false);
  };

  const handleCopy = async () => {
    void track(AMP_KEYS.STUDIO_ITEM_COPY_CLICK, {
      from: props.item.is_draft ? 'in_chat' : 'detail',
    });
    const htmlString = studioItemMarkdownToHtml(props.item.content.body, {
      stripCategories: true,
    });
    const plainText = stripStudioItemCategoryTags(props.item.content.body);

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([htmlString], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      }),
    ]);

    toast('Copied to clipboard', 'success');
  };

  const handleDownload = () => {
    exportStudioItemToPdf(props.item);
  };

  const handleRename = () => {
    props.setEditMode(true);
  };

  const handleDelete = () => {
    popup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete Report',
      message: 'Are you sure you want to delete this report?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        void (async () => {
          try {
            await props.onDelete(props.item.alias);
            toast('Report deleted', 'success');
            void wr.push('/reports');
          } catch (error) {
            console.error(error);
            toast('You do not have permission to delete.', 'fail');
          }
        })();
      },
    });
  };

  const handleSaveToStudio = async () => {
    void track(AMP_KEYS.STUDIO_ITEM_SAVE_TO_STUDIO_CLICK, {
      alias: props.item.alias,
    });

    try {
      const savedItem = await service.studio.SaveHeySynclyStudioItemFromChat(
        props.item.alias,
      );

      await queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });

      toast('Saved to Reports', 'success', 3000, {
        actionLabel: 'View detail',
        onClick: () => {
          void wr.push(`/reports/${savedItem.alias}`);
        },
      });
    } catch (error) {
      console.error(error);
      toast('Failed to save to studio', 'fail');
    }
  };

  const handleShare = () => {
    const targetLink = `${window.location.origin}/workspace/${workspaceId}/studio/${props.item.alias}`;

    overlay.open(({ isOpen, close, unmount }) => (
      <SharedLinkDialog
        title="Share Report"
        description="Manage the shared link settings for this report."
        targetId={props.item.alias}
        targetType="HEY_SYNCLY"
        targetLink={targetLink}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
      />
    ));
  };

  const quickActionItems = [
    {
      icon: <Share2 className="size-4" />,
      label: 'Share',
      onClick: handleShare,
    },
    {
      icon: <Copy className="size-4" />,
      label: 'Copy',
      onClick: handleCopy,
    },
    {
      icon: (
        <ThumbsUp className={cn('size-4', isUpVoted && 'text-green-500')} />
      ),
      label: 'Thumbs up',
      onClick: handleThumbsUp,
    },
    {
      icon: (
        <ThumbsDown className={cn('size-4', isDownVoted && 'text-red-500')} />
      ),
      label: 'Thumbs down',
      onClick: handleThumbsDown,
    },
  ] as const;

  if (props.item.is_draft)
    return (
      <div className="flex items-center gap-1">
        {!props.compactActions && (
          <>
            <div className="flex items-center gap-1 p-1 bg-background/80">
              <HeaderIconButton
                icon={<Share2 className="size-4" />}
                label="Share"
                onClick={handleShare}
              />
              <HeaderIconButton
                icon={<Copy className="size-4" />}
                label="Copy"
                onClick={handleCopy}
              />
              <HeaderIconButton
                icon={
                  <ThumbsUp
                    className={cn('size-4', isUpVoted && 'text-green-500')}
                  />
                }
                label="Thumbs up"
                onClick={handleThumbsUp}
              />
              <HeaderIconButton
                icon={
                  <ThumbsDown
                    className={cn('size-4', isDownVoted && 'text-red-500')}
                  />
                }
                label="Thumbs down"
                onClick={handleThumbsDown}
              />
            </div>
            <SourcePopover item={props.item} />
          </>
        )}
        {props.compactActions && (
          <>
            <SourcePopover item={props.item} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <span>
                  <HeaderIconButton
                    icon={<Ellipsis className="size-4" />}
                    label="More actions"
                    allowPropagation
                  />
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {quickActionItems.map((action) => (
                  <DropdownMenuItem key={action.label} onClick={action.onClick}>
                    {action.icon}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleDownload}>
            <Download className="size-4" />
            {!props.compactActions && 'Download PDF'}
          </Button>
          <Button size="sm" onClick={handleSaveToStudio}>
            <Plus className="size-4" />
            {!props.compactActions && 'Add to Reports'}
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center pr-1">
        <ProfileAvatar
          text={props.item.created_by}
          type={AvartarType.MEMBER}
          withText
          size={20}
          className="py-1.5 pl-2 pr-2"
          avatarFontSize="11px"
          textClassName="text-sm font-medium"
        />
        <span className="truncate px-3 py-1.5 text-sm font-medium text-foreground">
          {getTimezoneDayjs(props.item.created_at).format(DATE_FORMAT.readable)}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {props.compactActions && <SourcePopover item={props.item} />}
        {!props.compactActions && (
          <>
            <div className="flex items-center gap-1 p-1 bg-background/80">
              <HeaderIconButton
                icon={<Share2 className="size-4" />}
                label="Share"
                onClick={handleShare}
              />
              <HeaderIconButton
                icon={<Copy className="size-4" />}
                label="Copy"
                onClick={handleCopy}
              />
              <HeaderIconButton
                icon={
                  <ThumbsUp
                    className={cn('size-4', isUpVoted && 'text-green-500')}
                  />
                }
                label="Thumbs up"
                onClick={handleThumbsUp}
              />
              <HeaderIconButton
                icon={
                  <ThumbsDown
                    className={cn('size-4', isDownVoted && 'text-red-500')}
                  />
                }
                label="Thumbs down"
                onClick={handleThumbsDown}
              />
            </div>
            <SourcePopover item={props.item} />
            <Button variant="secondary" size="sm" onClick={handleDownload}>
              <Download className="size-4" />
              Download PDF
            </Button>
          </>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <span>
              <HeaderIconButton
                icon={<Ellipsis className="size-4" />}
                label="More actions"
                allowPropagation={true}
              />
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {props.compactActions &&
              quickActionItems.map((action) => (
                <DropdownMenuItem key={action.label} onClick={action.onClick}>
                  {action.icon}
                  {action.label}
                </DropdownMenuItem>
              ))}
            {props.compactActions && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={handleRename}>Rename</DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onClick={handleDelete}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

type HeaderIconButtonProps = Omit<
  ComponentPropsWithoutRef<typeof Button>,
  'aria-label' | 'children'
> & {
  icon: ReactNode;
  label: string;
  allowPropagation?: boolean;
};

const HeaderIconButton = (props: HeaderIconButtonProps) => {
  const {
    allowPropagation = false,
    className,
    icon,
    label,
    onClick,
    ...buttonProps
  } = props;

  return (
    <TooltipV2 tooltipNode={label}>
      <Button
        {...buttonProps}
        variant="ghost"
        size="icon-sm"
        className={cn(
          'rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground',
          className,
        )}
        onClick={(e) => {
          if (!allowPropagation) {
            e.stopPropagation();
          }
          void onClick?.(e);
        }}
        aria-label={label}
      >
        {icon}
      </Button>
    </TooltipV2>
  );
};

const SourcePopover = (props: { item: HeySynclyStudioItem }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span>
          <HeaderIconButton
            allowPropagation
            onClick={() => {
              void track(AMP_KEYS.STUDIO_ITEM_SOURCE_VIEW_CLICK, {
                from: props.item.is_draft ? 'in_chat' : 'detail',
              });
            }}
            icon={<Info className="size-4" />}
            label="View sources"
          />
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-100" align="end">
        <StudioItemSourceView item={props.item} />
      </PopoverContent>
    </Popover>
  );
};
